import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { lockBodyScroll } from '../../utils/bodyScrollLock';
import { usePopupTransition } from './popupTransition';
import './CoreboundDialog.css';

const CoreboundDialog = ({
  title,
  onClose,
  children,
  className = '',
  id,
  fullScreen = false,
}) => {
  const dialogRef = useRef(null);
  const closeRef = useRef(null);
  const { close, popupClass, closing } = usePopupTransition(onClose);
  useEffect(() => {
    const trigger = document.activeElement;
    const unlock = lockBodyScroll();
    const dialog = dialogRef.current;
    if (dialog.showModal) dialog.showModal();
    else dialog.setAttribute('open', '');
    closeRef.current?.focus({ preventScroll: true });
    return () => {
      unlock();
      if (trigger?.isConnected) trigger.focus?.({ preventScroll: true });
    };
  }, []);
  return createPortal(
    <dialog
      ref={dialogRef}
      id={id}
      className={`coreboundDialog ${popupClass} ${
        fullScreen ? 'is-fullscreen' : ''
      } ${className}`}
      aria-modal="true"
      aria-label={title}
      aria-busy={closing || undefined}
      onCancel={(event) => {
        event.preventDefault();
        close();
      }}
      onKeyDown={(event) => {
        event.stopPropagation();
        if (event.key === 'Escape') {
          event.preventDefault();
          close();
        }
        if (event.key !== 'Tab') return;
        const controls = [
          ...dialogRef.current.querySelectorAll(
            'button:not(:disabled), a[href], input:not(:disabled), [tabindex="0"]'
          ),
        ];
        const first = controls[0];
        const last = controls[controls.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }}
      onClick={(event) => {
        if (event.target !== event.currentTarget) return;
        const bounds = event.currentTarget.getBoundingClientRect();
        if (
          event.clientX < bounds.left ||
          event.clientX > bounds.right ||
          event.clientY < bounds.top ||
          event.clientY > bounds.bottom
        )
          close();
      }}
    >
      <header className="coreboundDialogHeader">
        <h2>{title}</h2>
        <button
          ref={closeRef}
          type="button"
          onClick={close}
          aria-label={`Close ${title}`}
        >
          Close ×
        </button>
      </header>
      <div className="coreboundDialogBody">
        {typeof children === 'function' ? children(close) : children}
      </div>
    </dialog>,
    document.body
  );
};

export default CoreboundDialog;
